/**
 * Derivações. Funções puras, sem I/O, sem React, sem banco.
 *
 * Tudo que é calculado a partir de medida bruta mora aqui ou numa view SQL — e
 * em lugar nenhum mais. Ver docs/decisoes/0004-nada-derivado-no-banco.md.
 */

/**
 * Idade em meses completos, sempre relativa à data da avaliação — nunca a hoje.
 * É por isso que o banco guarda `birth_date` e não `age`: a idade de um animal
 * numa ficha de 2026 não pode mudar porque o ano virou.
 */
export function idadeEmMeses(nascimento: string, naData: string): number {
  const n = new Date(`${nascimento}T00:00:00Z`);
  const d = new Date(`${naData}T00:00:00Z`);
  let meses = (d.getUTCFullYear() - n.getUTCFullYear()) * 12 + (d.getUTCMonth() - n.getUTCMonth());
  if (d.getUTCDate() < n.getUTCDate()) meses -= 1;
  return Math.max(0, meses);
}

/**
 * Faixa etária usada para agrupar contemporâneos.
 * Os cortes seguem os marcos de desenvolvimento do cavalo, não números redondos:
 * 36 meses é quando o crescimento em altura essencialmente cessa.
 */
export type FaixaEtaria = 'potro' | 'jovem' | 'adulto' | 'senior';

export function faixaEtaria(meses: number): FaixaEtaria {
  if (meses < 12) return 'potro';
  if (meses < 36) return 'jovem';
  if (meses < 180) return 'adulto';
  return 'senior';
}

export interface MedidasBrutas {
  A_CER?: number | null;
  A_GAR?: number | null;
  C_COR?: number | null;
  P_TOR?: number | null;
  PE_TOR?: number | null;
  PE_CAN?: number | null;
  A_SUB?: number | null;
}

export interface Indices {
  garupaCernelha: number | null;
  corporal: number | null;
  toracico: number | null;
  ossatura: number | null;
  profundidade: number | null;
}

const razao = (num?: number | null, den?: number | null, fator = 1): number | null =>
  num == null || den == null || den === 0 ? null : Number(((fator * num) / den).toFixed(4));

/** Índices morfométricos. Calculados na consulta, jamais armazenados. */
export function indices(m: MedidasBrutas): Indices {
  return {
    garupaCernelha: razao(m.A_GAR, m.A_CER),
    corporal: razao(m.C_COR, m.A_CER),
    toracico: razao(m.PE_TOR, m.A_CER),
    ossatura: razao(m.PE_CAN, m.A_CER, 100),
    profundidade: razao(m.P_TOR, m.A_CER),
  };
}

/** Velocidade a partir do bruto: distância entre cones dividida pelo tempo. */
export function velocidade(distanciaM: number | null, tempoS: number | null) {
  if (distanciaM == null || tempoS == null || tempoS <= 0) return { ms: null, kmh: null };
  const ms = distanciaM / tempoS;
  return { ms: Number(ms.toFixed(3)), kmh: Number((ms * 3.6).toFixed(2)) };
}

/**
 * Consistência geométrica: profundidade torácica + vazio subesternal devem
 * igualar a altura de cernelha. Duplicada aqui, em forma numérica, para uso em
 * telas — a regra que bloqueia o fechamento da ficha é a GEO-001 da spec.
 */
export function desvioGeometrico(m: MedidasBrutas): number | null {
  if (m.P_TOR == null || m.A_SUB == null || m.A_CER == null) return null;
  return Number((m.P_TOR + m.A_SUB - m.A_CER).toFixed(1));
}
